import { NodeData } from 'json-edit-react'
import React, { useCallback, useMemo } from 'react'
import { INode } from 'shared/types/tree'
import { objectTest, isNodeType } from 'shared/utils'
import { NodeIdMap } from '../../../version_mapping/utility'
import { getAncestorForCondition } from '../../NodeReplace/utility'

export const customText = {
  ITEM_SINGLE: itemCountReplacement,
  ITEMS_MULTIPLE: itemCountReplacement,
}

function itemCountReplacement({ value }: NodeData) {
  if (value instanceof Object && 'type' in value) {
    if ('title' in value) {
      return `${value.type}: ${value.title}`
    }
    return `${value.type}`
  }
  return null
}

export function searchFilterForTypes(types: string[]) {
  return (inputData: NodeData) => {
    const { key, value, path } = inputData
    if (key === 'description' && !value) return false

    if (
      ['nodes', 'leaves'].includes(String(key)) &&
      Array.isArray(value) &&
      !value.length
    )
      return false

    if (value === null) return false

    return !path.find((t) => types.includes(String(t)))
  }
}

export function useButtons(
  nodeButtons?: {
    Element: React.FC
    onClick: (node: INode) => void
  }[],
) {
  return useMemo(() => {
    return nodeButtons?.map(({ Element, onClick }) => {
      return {
        Element: ({ nodeData: { value } }: { nodeData: NodeData }) => {
          if (valueIsNode(value)) {
            return Element({})
          }
          return null
        },
        onClick: ({ value }: NodeData, e: React.MouseEvent) => {
          e.stopPropagation()
          if (valueIsNode(value)) {
            onClick(value)
          }
        },
      }
    })
  }, [nodeButtons])
}

export function useIsCollapsed(
  selectedNodeId: string | undefined,
  idMap: NodeIdMap | undefined,
) {
  return useCallback(
    (nodeData: NodeData) => {
      const { level, parentData, value, key } = nodeData
      if (level === 0) return false

      if (!selectedNodeId || !idMap) return true

      if (valueIsNode(value)) {
        return !(
          value.id === selectedNodeId ||
          getIsAncestor({
            idMap,
            parentNodeId: value.id,
            childNodeId: selectedNodeId,
          })
        )
      }

      if (key === 'nodes' && valueIsNode(parentData)) {
        return !getIsAncestor({
          idMap,
          parentNodeId: parentData.id,
          childNodeId: selectedNodeId,
        })
      }

      return true
    },
    [selectedNodeId, idMap],
  )
}

function getIsAncestor({
  idMap,
  childNodeId,
  parentNodeId,
}: {
  childNodeId: string
  parentNodeId: string
  idMap: NodeIdMap
}) {
  const childNode = idMap[childNodeId].node
  return !!getAncestorForCondition(
    childNode,
    ({ id }) => id === parentNodeId,
    idMap,
  )
}

function valueIsNode(value: unknown): value is INode {
  return objectTest(value) && 'type' in value && isNodeType(value.type)
}
